import { ACCOUNT_KYC_STATUS } from '../constants/kycStatus'
import { ACCOUNT_STATE } from '../constants/accountState'

// Account-level KYC states that block deposit / withdrawal / invest CTAs.
// Driven by the account-status query (kycStatus is AccountKycStatus); consumed
// by AppTopNav, wallet, bank accounts. Financial actions are enabled only once
// KYC is COMPLETED (or actively IN_REVIEW); every other account-level state
// blocks them. Includes PENDING (`isKycVerificationPending` below) so an
// investor whose KYC is pending can browse but cannot transact.
const DISABLED_FINANCIAL_ACTION_KYC_STATUSES = new Set<string>([
  ACCOUNT_KYC_STATUS.NO_KYC_REQUIRED,
  ACCOUNT_KYC_STATUS.PENDING,
  ACCOUNT_KYC_STATUS.REQUIRED,
])

export const shouldDisableFinancialActions = (kycStatus?: string | null): boolean =>
  kycStatus != null && DISABLED_FINANCIAL_ACTION_KYC_STATUSES.has(kycStatus)

// KYC pending verification — and ONLY KYC_PENDING. The investor can browse
// everything but cannot transact; the path forward is to complete (or redo)
// their KYC. Distinct from `shouldDisableFinancialActions` (broader — also
// covers onboarding-not-started, guest, and rejected states, which each have
// their own dedicated flows). This narrow gate drives the app-shell "KYC
// pending" banner and the per-action "Complete KYC" blockers, so guests /
// onboarding / rejected users never see a misplaced "Complete your KYC" prompt.
export const isKycVerificationPending = (kycStatus?: string | null): boolean =>
  kycStatus === ACCOUNT_KYC_STATUS.PENDING

// Three-way gate for money-moving surfaces (wallet deposit / withdraw), derived
// from the SAME server-owned `accountState` the dashboard + top-nav read (see
// useDashboardState / AppTopNav) — so the wallet never contradicts the
// "Under review" / "KYC verified" chip in the nav. We collapse the dashboard's
// active → invested | approved-ready split (both are 'verified' here) because
// the wallet doesn't need portfolio data to decide money-action access.
//   • 'verified'        ← ACTIVE — KYC approved.
//   • 'action-required' ← ONBOARDING_PENDING / GUEST / REKYC_EXPIRED /
//                         REKYC_REJECTED — the investor must (re)do their KYC.
//   • 'in-review'       ← IN_REVIEW, and any unexpected value (conservative
//                         default, mirroring the dashboard resolver) — KYC has
//                         been submitted and is awaiting review.
// PAUSED is intentionally not mapped: a paused account also carries
// accessRestricted=true, and every consuming surface gates on that first (the
// restriction notice takes precedence over this state).
export type KycActionState = 'verified' | 'in-review' | 'action-required'

export const resolveKycActionState = (accountState?: string | null): KycActionState => {
  switch (accountState) {
    case ACCOUNT_STATE.ACTIVE:
      return 'verified'
    case ACCOUNT_STATE.ONBOARDING_PENDING:
    case ACCOUNT_STATE.GUEST:
    case ACCOUNT_STATE.REKYC_EXPIRED:
    case ACCOUNT_STATE.REKYC_REJECTED:
      return 'action-required'
    case ACCOUNT_STATE.IN_REVIEW:
    default:
      return 'in-review'
  }
}
